import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../services/apiService';
import { trackClick } from '../../utils/analytics';
import { useUpgradeGate } from '../useUpgradeGate';
import { ChangeTechs } from './ChangeTechs';
import { PlatformUpdates } from './PlatformUpdates';
import { WorldNews } from './WorldNews';
import {
  type WnChange, type WnFetcher, type WnItem, type WnNewUniversity, type WnPayload, type WnQuery,
  daysAgo, hostOf,
} from './types';

/**
 * What's New (left rail, after For You; App.tsx view 'whatsnew').
 * Layout from Spec-Docs/whatsnew-prototype, in the site's colours and DM Sans.
 *
 *   Header        title + subtitle (admin-editable texts)
 *   New on Cosmos the last data refresh, new universities, IP added / removed
 *                 at existing universities (Backend/whatsnew.py platform_updates)
 *   In the world  curated news: category tabs with counts, industry, dates,
 *                 grouped per category
 *
 * Data: GET /api/v1/whatsnew (signed-in members, bearer-checked). The admin's
 * Preview renders this same component with a `fetcher` that reads drafts too
 * (and tracks nothing).
 *
 * Tracking (trackClick → PostHog + site_events): whatsnew_view,
 * whatsnew_section_view, whatsnew_university_explore, whatsnew_change_open,
 * whatsnew_tab, whatsnew_industry, whatsnew_dates, whatsnew_item_source,
 * whatsnew_view_all.
 */

interface Props {
  /** Open Cosmos (Direct Search) filtered to a university. */
  onOpenCosmos?: (code: string, name: string) => void;
  /** Open a technology in the app's tech card (the new tech offers listed here). */
  onOpenTech?: (id: string) => void;
  /** Admin preview: where the data comes from (drafts included). */
  fetcher?: WnFetcher;
  preview?: boolean;
}

const memberFetcher: WnFetcher = async (q) => {
  const uid = apiService.getStoredUserId();
  if (!uid) return { data: null, error: 'Please sign in again to see What\'s New.' };
  const r = await apiService.getWhatsNew(uid, q);
  if (r.success && r.data?.success) return { data: r.data as WnPayload, error: '' };
  return { data: null, error: (r.data as any)?.detail || r.error || 'Could not load What\'s New.' };
};

const EMPTY_Q: WnQuery = { category: 'all', industry: '', from: '', to: '' };

const Skeleton = () => (
  <div className="flex flex-col gap-6 animate-pulse" aria-hidden="true">
    <div className="h-6 w-56 rounded bg-primary-100" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="h-24 rounded-xl bg-primary-100/70" /><div className="h-24 rounded-xl bg-primary-100/70" />
    </div>
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      {[0, 1, 2, 3].map(i => <div key={i} className="h-40 rounded-xl bg-primary-100/60" />)}
    </div>
    <div className="h-6 w-64 rounded bg-primary-100" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
      <div className="h-44 rounded-xl bg-primary-100/60" /><div className="h-44 rounded-xl bg-primary-100/60" />
    </div>
  </div>
);

export const WhatsNewPage: React.FC<Props> = ({ onOpenCosmos, onOpenTech, fetcher, preview = false }) => {
  const [openChange, setOpenChange] = useState<WnChange | null>(null);
  // Explorers (free plan) get the plans sheet instead of opening new
  // universities / new tech offers (tracker, 2026-10-29). The admin preview
  // is never gated.
  const upgrade = useUpgradeGate();
  const paidOnly = (feature: string, ctx: Record<string, unknown>, action: () => void) => {
    if (preview) { action(); return; }
    upgrade.gate(!!upgrade.ent.paid, feature, action, undefined, { surface: 'whatsnew', ...ctx });
  };
  const load = fetcher || memberFetcher;
  const [data, setData] = useState<WnPayload | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState<WnQuery>(EMPTY_Q);
  const seq = useRef(0);
  const worldRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);

  const track = useCallback((action: string, props?: Record<string, unknown>) => {
    if (!preview) trackClick(action, props);
  }, [preview]);

  const fetchData = useCallback(async (q: WnQuery) => {
    const mine = ++seq.current;
    setLoading(true);
    const { data: d, error: e } = await load(q);
    if (mine !== seq.current) return;
    setLoading(false);
    if (d) { setData(d); setError(''); } else setError(e);
    return d;
  }, [load]);

  // First load: the page's default date range (admin setting) once known.
  useEffect(() => {
    (async () => {
      const d = await fetchData(EMPTY_Q);
      const days = d?.settings?.world_default_days || 0;
      if (days > 0) {
        const q = { ...EMPTY_Q, from: daysAgo(days), to: daysAgo(0) };
        setQuery(q);
        void fetchData(q);
      }
    })();
    track('whatsnew_view', { surface: 'app' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Section views: once each, when half the section header is on screen.
  useEffect(() => {
    if (preview || !data || typeof IntersectionObserver === 'undefined') return;
    const seen = new Set<string>();
    const els = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-wn-section]') || []);
    const io = new IntersectionObserver(entries => {
      entries.forEach(en => {
        const k = (en.target as HTMLElement).dataset.wnSection || '';
        if (en.isIntersecting && k && !seen.has(k)) {
          seen.add(k);
          trackClick('whatsnew_section_view', { section: k });
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.15 });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
    // Re-observe only when the sections themselves appear / disappear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, !!data, !!data?.platform, !!data?.world]);

  const apply = (q: WnQuery) => { setQuery(q); void fetchData(q); };

  const onTab = (key: string) => {
    if (key === query.category) return;
    track('whatsnew_tab', { category: key });
    apply({ ...query, category: key });
  };
  const onIndustry = (v: string) => {
    track('whatsnew_industry', { industry: v || 'all' });
    apply({ ...query, industry: v });
  };
  const onDates = (from: string, to: string) => {
    // A half-typed date (an empty value mid-edit) waits for a whole one.
    if ((from && !/^\d{4}-\d{2}-\d{2}$/.test(from)) || (to && !/^\d{4}-\d{2}-\d{2}$/.test(to))) return;
    track('whatsnew_dates', { from: from || null, to: to || null });
    apply({ ...query, from, to });
  };
  const onViewAll = (key: string) => {
    track('whatsnew_view_all', { category: key });
    apply({ ...query, category: key });
    worldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const onSource = (it: WnItem) => {
    track('whatsnew_item_source', { item_id: String(it.id), title: it.title, category: it.category,
                                    type_label: it.type_label, host: hostOf(it.source_url) });
  };
  const onClear = () => apply(EMPTY_Q);

  // The public university page, in a new tab so the app stays where it is.
  const openPage = (path: string) => { window.open(path, '_blank', 'noopener'); };
  const onExplore = (u: WnNewUniversity) => {
    track('whatsnew_university_explore', { code: u.code, name: u.name, target: u.path ? 'page' : 'cosmos' });
    paidOnly('new universities on Cosmos', { control: 'university_explore', code: u.code }, () => {
      if (u.path) openPage(u.path);
      else onOpenCosmos?.(u.code, u.name);
    });
  };
  const onChange = (c: WnChange) => {
    // Both open the change's list here (tracker, 2026-10-29): the new tech
    // offers with save / share (Explorers get the plans sheet), or the titles
    // of the removed ones.
    track('whatsnew_change_open', { code: c.code, name: c.name, kind: c.kind, n: c.n, target: 'list' });
    if (c.kind === 'added') {
      paidOnly('new tech offers', { control: 'change_open', code: c.code }, () => setOpenChange(c));
    } else setOpenChange(c);
  };

  const s = data?.settings;
  return (
    <main ref={rootRef}
      className="flex flex-col flex-1 self-stretch rounded-2xl bg-white shadow-custom-light overflow-y-auto hide-scrollbar">
      <div className="w-full max-w-[1120px] mx-auto px-4 sm:px-6 py-6 sm:py-10 flex flex-col gap-10 text-grayscale-1300">
        <header className="flex flex-col gap-1.5">
          <h1 className="m-0 text-[28px] sm:text-[34px] font-bold tracking-[-0.01em]">{s?.texts.title || 'What\'s New'}</h1>
          <p className="m-0 text-base text-grayscale-1000">
            {s?.texts.subtitle || 'Latest from the Cosmos platform and the world of IP & licensing'}
          </p>
          {preview && (
            <span className="self-start mt-1 text-[11px] font-semibold uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-800">
              Preview · drafts included
            </span>
          )}
        </header>

        {!data && loading && <Skeleton />}
        {!data && !loading && error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-6 text-sm text-red-700 flex flex-col gap-3 items-start">
            <span>{error}</span>
            <button type="button" onClick={() => void fetchData(query)}
              className="text-sm font-medium text-primary-700 hover:underline underline-offset-2">Try again</button>
          </div>
        )}

        {data && s && (
          <>
            {data.platform && (
              <PlatformUpdates settings={s} platform={data.platform} onExplore={onExplore} onChange={onChange} />
            )}
            {openChange && (
              <ChangeTechs change={openChange} onClose={() => setOpenChange(null)}
                           onOpenTech={onOpenTech ? id => { setOpenChange(null); onOpenTech(id); } : undefined}
                           onOpenCosmos={onOpenCosmos} />
            )}
            {data.world && (
              <div ref={worldRef} className="scroll-mt-4">
                <WorldNews settings={s} world={data.world} query={query} loading={loading}
                  onTab={onTab} onIndustry={onIndustry} onDates={onDates} onViewAll={onViewAll}
                  onSource={onSource} onClear={onClear} track={track} />
              </div>
            )}
            {!data.platform && !data.world && (
              <div className="rounded-xl border border-dashed border-primary-300 px-5 py-8 text-sm text-grayscale-1000 text-center">
                Nothing to show here right now.
              </div>
            )}
            {error && (
              <div className="text-sm text-red-700">{error}</div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default WhatsNewPage;
